import React, { useEffect } from 'react';
import {Input, TextArea} from '@progress/kendo-react-inputs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import {DatePickerChangeEvent} from '@progress/kendo-react-dateinputs';
import { Card, CardActions, CardBody, CardHeader, CardTitle } from '@progress/kendo-react-layout';
import { Badge } from '@progress/kendo-react-indicators';
import styles from './_funding-page.module.scss';
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../store/rootReducer";
import { ApplicationFunding } from '../../../../Interfaces/Application/ApplicationFunding';
import { IQuote } from "../../../../Interfaces/Common/IQuote";
import { updateEditingApp } from '../../../../store/application/actions';
import { useBusinessRulesEngine } from '../../../../Data/BusinessRulesData';
import produce from 'immer';
import {
  areAmountFinancedValuesEqual,
  getAmsCodeForReserve,
  isAmountFinancedCompliant, isAprCompliant, isCarletonAprCompliant
} from "../../../Quoters/IndirectQuoter/IndirectQuoterUtils";
import { StatusWorkflowComponent } from '../StatusWorkflowComponent';
import { useStatusWorkflowUtils } from '../../../../hooks/UseStatusWorkflowUtils';
import {getAmsLookupsToDisplay} from "../AmsLoanPostingDialog/AmsLoanPostingUtils";
import { indirectStipulationStatuses } from '../../../../utils/constants';
import { loadIndirectStipsAndDecisionReasons } from '../../../../store/dealerService/actions';
import {Button} from "@mui/material";
import {getCarletonAprForApplicationFunding} from "../../../../store/application/types";
import {
  getEnumForValue,
  getOptionsFromEnumAndLoanClass,
  hasValue,
  NoScrollNumericTextBox,
  DatePickerWithYearSanitizer
} from "@amsanalytics/react-shared-library";
import {Error} from "@progress/kendo-react-labels";
import {useEmployeeRolePermissions} from "../../../../Data/EmployeeRoleData";
import { usePostedAppLock } from "../../../../hooks/usePostedAppLock";
import ErrorDialog from "../../../Dialogs/ErrorDialog";
import {useDialog} from "../../../../providers/DialogProvider";
import CalculateIcon from "@mui/icons-material/Calculate";
import {IConfiguration} from "../../../../Data/ConfigurationData";
import { isValidDate } from '../../../../utils/utils';

interface FundingPageProps {
  underwriterQuote: IQuote;
  configuration: IConfiguration;
}

// Main component
export default function FundingPage(props: FundingPageProps) {
  const [openDialog, closeDialog] = useDialog();
  const dispatch = useDispatch();
  const businessRulesEngine = useBusinessRulesEngine();
  const currentApp = useSelector((state: AppState) => state.applications.editingApp!);
  const applicationFunding = currentApp?.applicationFunding;
  const lookups = useSelector((state: AppState) => state.applications.lookups);
  const organizations = lookups.organizations;
  const organizationForApp = organizations.find(o => o.id.toLowerCase() === currentApp.organizationId?.toLowerCase());
  const loanTypes = lookups.loanTypes;
  const loanTypesToDisplay = getAmsLookupsToDisplay(lookups.loanTypes);
  const amsCodeBasedOnReserve = getAmsCodeForReserve(currentApp, organizations);
  const vehicle = currentApp.vehicle;
  const tradeInVehicle = currentApp.tradeInVehicle;
  const indirectStipulations = useSelector((state: AppState) => state.dealerService.applicationStipulations);
  const dealerServiceAppHistoryItems = useSelector((state: AppState) => state.applications.dealerServiceAppHistoryItems);
  const paymentFrequencies = getOptionsFromEnumAndLoanClass(lookups.carletonItems, "PaymentFrequency", '');
  const selectedPaymentFrequency = getEnumForValue(paymentFrequencies, applicationFunding?.paymentFrequency);

  const [canView, canEdit, canAct, canActInStatus] = useEmployeeRolePermissions();
  const { activeUnlock } = usePostedAppLock();
  const canEditInStatus = currentApp?.unlockedBySuperUser || !!activeUnlock || canActInStatus(currentApp?.appStatusId || "", "Save");

  const getAmountFinanced = (funding: ApplicationFunding | undefined): number => {
    return (funding?.salesPrice ?? 0)
      - (funding?.rebate ?? 0)
      + (funding?.dealerDocFee ?? 0)
      + (funding?.dmvFee ?? 0)
      + (funding?.miscFee ?? 0)
      + (funding?.salesTax ?? 0)
      + (funding?.creditLife ?? 0)
      + (funding?.ah ?? 0)
      + (funding?.serviceContracts ?? 0)
      + (funding?.gap ?? 0)
      - (funding?.cashDown ?? 0)
      - (funding?.netTrade ?? 0);
  }
  
  useEffect(() => {
    if (currentApp ) {
      dispatch(loadIndirectStipsAndDecisionReasons(currentApp.id))
    }
  }, [currentApp, dealerServiceAppHistoryItems]);

  // On initial load, if the applicationFunding object is null, set it to default values.
  if (!applicationFunding) {
    // update currentApp with new funding object using default values
    const newApp = produce(currentApp, draft => {
      draft.applicationFunding = {
        applicationId: currentApp.id,
        
        // Overview
        loanTypeId: loanTypes.find(lt => lt.id === organizationForApp?.loanTypeId)?.id,
        contractDate: new Date(),

        // Vehicles
        vehicleId: currentApp.vehicleId,
        tradeInVehicleId: currentApp.tradeInVehicleId,
        tradeInVehicle2Id: currentApp.tradeInVehicle2Id,

        // Compliance
        aprCompliant: isAprCompliant(props.underwriterQuote.apr, props.underwriterQuote.apr, props.configuration), // both are equal at this point
        carletonAprCompliant: isCarletonAprCompliant(undefined, props.configuration),
        amountFinancedEqualCompliant: false,
        amountFinancedCompliant: false,

        // Payment Schedule
        paymentFrequency: undefined,
        numberOfPayments: undefined,
        amountOfPayments: undefined,
        firstDueDate: undefined,
        finalDueDate: undefined,
        quotePayTypeId: props.underwriterQuote.quotePayTypeId,

        // Other
        amsBegDisc: undefined,

        // Disclosures
        disclosureApr: props.underwriterQuote.apr ?? 0,
        financeCharge: undefined,
        disclosureAmountFinanced: undefined,
        totalOfPayments: undefined,
        downPayment: undefined,
        totalSalesPrice: undefined,

        // Itemization
        salesPrice: undefined,
        rebate: undefined,
        dealerDocFee: undefined,
        dmvFee: undefined,
        miscFee: undefined,
        salesTax: undefined,
        creditLife: undefined,
        ah: undefined,
        serviceContracts: undefined,
        gap: undefined,
        cashDown: undefined,
        netTrade: undefined,
        unpaidBalance: undefined,
        amountFinanced: getAmountFinanced(applicationFunding),
        term: undefined,
        apr: props.underwriterQuote.apr ?? 0,
        payment: undefined,

        modifiedBy: undefined,
        modifiedDate: undefined,
      };
    });
    dispatch(updateEditingApp(newApp, businessRulesEngine));
  }

  const getStatusBadge = (isCompliant: boolean | undefined) => {
    return isCompliant
      ? <Badge themeColor="success" className={styles.badge}>{"Yes"}</Badge>
      : <Badge themeColor="error" className={styles.badge}>{'Noncompliant'}</Badge>;
  };

  // Generic onChange handler for all form fields
  const handleChange = (field: keyof ApplicationFunding, value: any) => {
    // update currentApp with new value for the field, then dispatch editingApp
    const newApp = produce(currentApp, draft => {
      // if field exists, update it
      if (draft.applicationFunding && draft.applicationFunding.hasOwnProperty(field)) {
        (draft.applicationFunding as any)[field] = value;
        draft.applicationFunding.amountFinanced = getAmountFinanced(draft.applicationFunding); // calculating the amount financed every time one of the inputs is changed
        draft.applicationFunding.amountFinancedCompliant = isAmountFinancedCompliant(props.underwriterQuote, draft.applicationFunding);
        draft.applicationFunding.amountFinancedEqualCompliant = areAmountFinancedValuesEqual(draft.applicationFunding);
        draft.applicationFunding.carletonAprCompliant = isCarletonAprCompliant(draft.applicationFunding, props.configuration);
        draft.applicationFunding.aprCompliant = isAprCompliant(props.underwriterQuote.apr, draft.applicationFunding.disclosureApr, props.configuration);
      }
    });
    // Dispatch the new app to the store
    dispatch(updateEditingApp(newApp, businessRulesEngine));
  };

  const getUnpaidBalance = (): number => {
    return (applicationFunding?.salesPrice ?? 0)
      - (applicationFunding?.rebate ?? 0)
      + (applicationFunding?.dealerDocFee ?? 0)
      + (applicationFunding?.dmvFee ?? 0)
      + (applicationFunding?.miscFee ?? 0)
      + (applicationFunding?.salesTax ?? 0)
      - (applicationFunding?.cashDown ?? 0)
      - (applicationFunding?.netTrade ?? 0);
  }

  const areIndirectStipulationsMet = (): boolean => {  
    // If indirectStipulations is undefined or empty, consider condition met
    if (!indirectStipulations || indirectStipulations.length === 0) {
      return true;
    }
    
    // Check if all stipulations have the 'passed' status
    return indirectStipulations.every(stipulation => 
      (stipulation.statusId?.toUpperCase() === indirectStipulationStatuses.passed || stipulation.statusId === indirectStipulationStatuses.waived));
  }
  
  const workflowUtilStatuses = useStatusWorkflowUtils();
  const canFinalizeAndPost = applicationFunding?.amountFinancedCompliant 
    && applicationFunding?.amountFinancedEqualCompliant 
    && applicationFunding?.aprCompliant 
    && applicationFunding?.carletonAprCompliant;
  
  const conditionallyCalculateCarletonApr= (): void => {
    if (canEditInStatus) {
      dispatch(getCarletonAprForApplicationFunding(applicationFunding!));
    } else {
      openDialog({
        fullScreen: false,
        children: (
          <ErrorDialog
            title='Cannot Calculate In Status'
            errors={['The APR cannot be calculated in the current application status.']}
            closeDialog={closeDialog}
          />
        ),
      });
    }
  }
  
  return (
    <div className={styles.container}>
      <div className={styles.columnsContainer}>
        <div className={styles.twoColumnLayout}>
          {/* Left Column */}
          <div className={styles.column}>
            <div className={styles.sectionContainer}>
              <Card className={styles.sectionCard}>
                <CardHeader className={styles.cardHeader}>
                  <CardTitle className={styles.cardTitle}>Overview</CardTitle>
                </CardHeader>
                <CardBody className={styles.cardBody}>
                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>AMS Loan Type</label>
                    <div className={styles.formField}>
                      <DropDownList
                        data={loanTypesToDisplay}
                        textField={'description'}
                        dataItemKey={'id'}
                        value={loanTypesToDisplay.find(lt => lt.id === applicationFunding?.loanTypeId)}
                        onChange={(e) => handleChange('loanTypeId', e.value.id)}
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Contract Date</label>
                    <div className={styles.formField}>
                      <DatePickerWithYearSanitizer
                        value={isValidDate(applicationFunding?.contractDate) ? new Date(applicationFunding!.contractDate!) : null}
                        onChange={(event: DatePickerChangeEvent) => {
                          handleChange('contractDate', event.value);
                        }}
                        format="MM/dd/yyyy"
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>
                      Dealer Name <span className={styles.requiredMark}>*</span>
                    </label>
                    <div className={styles.formField}>
                      <Input
                        value={organizationForApp?.name}
                        readOnly
                        required={true}
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Dealer #</label>
                    <div className={styles.formField}>
                      <Input
                        value={amsCodeBasedOnReserve}
                        className={styles.formControl}
                        readOnly
                      />
                    </div>
                  </div>
                </CardBody>
              </Card>

              {/*Vehicle Info Section*/}
              <Card className={styles.sectionCard}>
                <CardHeader className={styles.cardHeader}>
                  <CardTitle className={styles.cardTitle}>Purchase Vehicle Info</CardTitle>
                </CardHeader>
                <CardBody className={styles.cardBody}>
                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>
                      Type <span className={styles.requiredMark}>*</span>
                    </label>
                    <div className={styles.formField}>
                      <DropDownList
                        data={['New', 'Used']}
                        value={'Used'}
                        // onChange={(e) => handleChange('vehicleType', e.value)}
                        disabled
                        className={styles.formControl}
                        required
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Vehicle Year</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={vehicle?.year}
                        // onChange={(e) => handleChange('vehicleYear', e.value)}
                        disabled
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Vehicle Make</label>
                    <div className={styles.formField}>
                      <Input
                        value={vehicle?.make}
                        // onChange={(e) => handleChange('vehicleMake', e.value)}
                        disabled
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Vehicle Model</label>
                    <div className={styles.formField}>
                      <Input
                        value={vehicle?.model}
                        // onChange={(e) => handleChange('vehicleModel', e.value)}
                        disabled
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Vehicle Trim</label>
                    <div className={styles.formField}>
                      <Input
                        value={undefined}
                        // onChange={(e) => handleChange('vehicleTrim', e.value)}
                        disabled
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Vehicle VIN</label>
                    <div className={styles.formField}>
                      <Input
                        value={vehicle?.vin}
                        // onChange={(e) => handleChange('vehicleVin', e.value)}
                        disabled
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Vehicle Mileage</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={vehicle?.mileage}
                        // onChange={(e) => handleChange('vehicleMileage', e.value)}
                        disabled
                        format="n0"
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Vehicle Color</label>
                    <div className={styles.formField}>
                      <Input
                        value={vehicle?.color}
                        // onChange={(e) => handleChange('vehicleColor', e.value)}
                        disabled
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>AMS Stock Number</label>
                    <div className={styles.formField}>
                      <Input
                        value={vehicle?.stockNumber}
                        // onChange={(e) => handleChange('stockNumber', e.value)}
                        disabled
                        className={styles.formControl}
                      />
                    </div>
                  </div>
                </CardBody>
              </Card>

              {/*Payment Schedule Section*/}
              <Card className={styles.sectionCard}>
                <CardHeader className={styles.cardHeader}>
                  <CardTitle className={styles.cardTitle}>Payment Schedule</CardTitle>
                </CardHeader>
                <CardBody className={styles.cardBody}>
                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Payment Frequency</label>
                    <div className={styles.formField}>
                      <DropDownList
                        data={paymentFrequencies}
                        textField={'option'}
                        dataItemKey={'value'}
                        value={selectedPaymentFrequency}
                        onChange={(e) => handleChange('paymentFrequency', e.value.value)}
                        className={styles.formControl}
                      />
                    </div>
                  </div>
                  
                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Number of Payments</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.numberOfPayments}
                        onChange={(e) => handleChange('numberOfPayments', e.value)}
                        format="n0"
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Amount of Payments</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.amountOfPayments}
                        onChange={(e) => handleChange('amountOfPayments', e.value)}
                        format="c2"
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>First Due Date</label>
                    <div className={styles.formField}>
                      <DatePickerWithYearSanitizer
                        value={isValidDate(applicationFunding?.firstDueDate) ? new Date(applicationFunding!.firstDueDate!) : null}
                        onChange={(event: DatePickerChangeEvent) => handleChange('firstDueDate', event.value)}
                        format="MM/dd/yyyy"
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Final Due Date</label>
                    <div className={styles.formField}>
                      <DatePickerWithYearSanitizer
                        value={isValidDate(applicationFunding?.finalDueDate) ? new Date(applicationFunding!.finalDueDate!) : null}
                        onChange={(event: DatePickerChangeEvent) => handleChange('finalDueDate', event.value)}
                        format="MM/dd/yyyy"
                        className={styles.formControl}
                      />
                    </div>
                  </div>
                </CardBody>
              </Card>

              {/*Trade In Section*/}
              <Card className={styles.sectionCard}>
                <CardHeader className={styles.cardHeader}>
                  <CardTitle className={styles.cardTitle}>Trade In Vehicle Info</CardTitle>
                </CardHeader>
                <CardBody className={styles.cardBody}>
                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Trade Year</label>
                    <div className={styles.formField}>
                      <Input
                        value={tradeInVehicle?.year}
                        disabled
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Trade Make</label>
                    <div className={styles.formField}>
                      <Input
                        value={tradeInVehicle?.make}
                        disabled
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Trade Model</label>
                    <div className={styles.formField}>
                      <Input
                        value={tradeInVehicle?.model}
                        disabled
                        className={styles.formControl}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Trade VIN</label>
                    <div className={styles.formField}>
                      <Input
                        value={tradeInVehicle?.vin}
                        disabled
                        className={styles.formControl}
                      />
                    </div>
                  </div>
                </CardBody>
              </Card>

              {/* Other Section
              <Card className={styles.sectionCard}>
                <CardHeader className={styles.cardHeader}>
                  <CardTitle className={styles.cardTitle}>Other</CardTitle>
                </CardHeader>
                <CardBody className={styles.cardBody}>
                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>AMS BEG_DISC</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.amsBegDisc}
                        disabled={true}
                        format="c2"
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>
                </CardBody>
              </Card> */}
            </div>
          </div>

          {/* Right Column */}
          <div className={styles.column}>
            <div className={styles.sectionContainer}>
              {/*Disclosures Section*/}
              <Card className={styles.sectionCard}>
                <CardHeader className={styles.cardHeader}>
                  <CardTitle className={styles.cardTitle}>Disclosures</CardTitle>
                </CardHeader>
                <CardBody className={styles.cardBody}>
                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Disclosure APR</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.disclosureApr}
                        onChange={(e) => handleChange('disclosureApr', e.value)}
                        max={100}
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Carleton APR</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.carletonCalculatedApr}
                        disabled
                        max={100}
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>

                  {hasValue(applicationFunding?.carletonExceptionMessage) &&
                    <div className={styles.formRow}>
                      <label className={styles.formLabel}>Error Message</label>
                      <div className={styles.formField}>
                        <TextArea
                          readOnly
                          autoSize
                          valid={false}
                          value={applicationFunding?.carletonExceptionMessage}
                        />
                      </div>
                    </div>
                  }

                  {applicationFunding?.carletonQuoteErrorMessage &&
                    <div className={styles.formRow}>
                      <label className={styles.formLabel}>Carleton Error</label>
                      <div className={styles.formField}>
                        <Input
                          readOnly
                          valid={false}
                          value={`#${applicationFunding.carletonQuoteErrorMessage.code}- ${applicationFunding.carletonQuoteErrorMessage.description}`}
                          type='text'
                        />
                      </div>
                    </div>
                  }
                  {applicationFunding?.carletonQuoteErrorMessage &&
                    <div className={styles.formRow}>
                      <label className={styles.formLabel}>Troubleshooting Steps</label>
                      <div className={styles.formField}>
                        <Error style={{color: "red", fontSize: 12}}>Troubleshooting Steps</Error>
                        <TextArea
                          readOnly
                          autoSize
                          valid={false}
                          value={`${applicationFunding.carletonQuoteErrorMessage.troubleshootingSteps}`}
                        />
                      </div>
                    </div>
                  }

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Finance Charge</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.financeCharge}
                        onChange={(e) => handleChange('financeCharge', e.value)}

                        format="c2"
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Disclosure Amount Financed</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.disclosureAmountFinanced}
                        onChange={(e) => handleChange('disclosureAmountFinanced', e.value)}

                        format="c2"
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Total of Payments</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.totalOfPayments}
                        onChange={(e) => handleChange('totalOfPayments', e.value)}

                        format="c2"
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Down Payment</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.downPayment}
                        onChange={(e) => handleChange('downPayment', e.value)}

                        format="c2"
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Total Sales Price</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.totalSalesPrice}
                        onChange={(e) => handleChange('totalSalesPrice', e.value)}

                        format="c2"
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>
                </CardBody>
              </Card>

              {/*Compliance Section*/}
              <Card className={styles.sectionCard}>
                <CardHeader className={styles.cardHeader}>
                  <CardTitle className={styles.cardTitle}>Calculation Check</CardTitle>
                </CardHeader>
                <CardBody className={styles.validationCardBody}>
                  <div className={styles.validationRow}>
                    <label className={styles.formLabel}>Amount Financed Compliant?</label>
                    {getStatusBadge(applicationFunding?.amountFinancedCompliant)}
                  </div>

                  <div className={styles.validationRow}>
                    <label className={styles.formLabel}>Amount Financed Equal?</label>
                    {getStatusBadge(applicationFunding?.amountFinancedEqualCompliant)}
                  </div>

                  <div className={styles.validationRow}>
                    <label className={styles.formLabel}>APR Compliant?</label>
                    {getStatusBadge(applicationFunding?.aprCompliant)}
                  </div>

                  <div className={styles.validationRow}>
                    <label className={styles.formLabel}>Carleton APR Compliant?</label>
                    {getStatusBadge(applicationFunding?.carletonAprCompliant)}
                    <Button
                      title={'Solve'}
                      color="primary"
                      variant='contained'
                      startIcon={<CalculateIcon style={{marginRight: '-2px'}} fontSize='small' />}
                      onClick={conditionallyCalculateCarletonApr}
                    >
                      Solve
                    </Button>
                  </div>
                </CardBody>
              </Card>

              {/*Itemization Section*/}
              <Card className={styles.sectionCard}>
                <CardHeader className={styles.cardHeader}>
                  <CardTitle className={styles.cardTitle}>Itemization</CardTitle>
                </CardHeader>
                <CardBody className={styles.cardBody}>
                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Sales Price</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.salesPrice}
                        onChange={(e) => handleChange('salesPrice', e.value)}
                        format="c2"
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Rebate</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.rebate}
                        onChange={(e) => handleChange('rebate', e.value)}
                        format="c2"
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Dealer Doc Fee</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.dealerDocFee}
                        onChange={(e) => handleChange('dealerDocFee', e.value)}
                        format="c2"
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>DMV Fee</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.dmvFee}
                        onChange={(e) => handleChange('dmvFee', e.value)}
                        format="c2"
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Misc Fee</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.miscFee}
                        onChange={(e) => handleChange('miscFee', e.value)}
                        format="c2"
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Sales Tax</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.salesTax}
                        onChange={(e) => handleChange('salesTax', e.value)}
                        format="c2"
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Credit Life</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.creditLife}
                        onChange={(e) => handleChange('creditLife', e.value)}
                        format="c2"
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>A/H</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.ah}
                        onChange={(e) => handleChange('ah', e.value)}
                        format="c2"
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Service Contracts</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.serviceContracts}
                        onChange={(e) => handleChange('serviceContracts', e.value)}
                        format="c2"
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Gap</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.gap}
                        onChange={(e) => handleChange('gap', e.value)}
                        format="c2"
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Cash Down</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.cashDown}
                        onChange={(e) => handleChange('cashDown', e.value)}

                        format="c2"
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Net Trade</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.netTrade}
                        onChange={(e) => handleChange('netTrade', e.value)}
                        format="c2"
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Unpaid Balance</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={getUnpaidBalance()}
                        format="c2"
                        className={styles.formControl}
                        spinners={false}
                        disabled={true}
                        aria-disabled={true}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Amount Financed</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.amountFinanced}
                        format="c2"
                        className={styles.formControl}
                        spinners={false}
                        disabled={true}
                        aria-disabled={true}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Term</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.term}
                        onChange={(e) => handleChange('term', e.value)}
                        format="n0"
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>APR</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.apr}
                        onChange={(e) => handleChange('apr', e.value ?? 0)}
                        max={100}
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.formLabel}>Payment</label>
                    <div className={styles.formField}>
                      <NoScrollNumericTextBox
                        value={applicationFunding?.payment}
                        onChange={(e) => handleChange('payment', e.value)}
                        format="c2"
                        className={styles.formControl}
                        spinners={false}
                      />
                    </div>
                  </div>
                </CardBody>
                <CardActions>
                  {/* <Button variant="contained" disabled={!canPostLoanToAMS()} onClick={() => alert("Posting loan to AMS...")}>
                    Post Loan to AMS
                  </Button> */}
                  {workflowUtilStatuses.preventDeskingOrPosting === false && canFinalizeAndPost && areIndirectStipulationsMet() && (
                    <StatusWorkflowComponent
                      style={{marginTop: '10px'}} 
                      disableDeskingOrPosting={workflowUtilStatuses.preventDeskingOrPosting}              
                    />
                  )}                  
                </CardActions>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};